import { db } from "../lib/firebase";
import { collection, getDocs, getDoc, doc, addDoc, serverTimestamp, query, where, orderBy, limit, updateDoc, increment } from "firebase/firestore";

import { uploadListingImage } from "./storage";
import { User } from "firebase/auth";
import { deleteDoc } from 'firebase/firestore';
import { getUserRole } from "./auth";
import { notifyAdmins } from "./email";

export interface Listing {
  id?: string;
  title: string;
  price: number;
  originalPrice?: number;
  seller?: string;
  sellerId?: string; // Firebase user ID of the seller
  rating?: number;
  location?: string;
  distance?: string;
  image?: string;
  condition?: string;
  verified?: boolean;
  timePosted?: string;
  category?: string;
  description?: string;
  images: string[]; // Make images a required array of strings
  // Extended fields
  specifications?: { [key: string]: string };
  views?: number;
  createdAt?: any;
  title_lower?: string;
  category_lower?: string;
  tags?: string[];
  type?: 'item' | 'house' | 'ticket';
  soldOut?: boolean;
  ticketTiers?: { name: string; price: number; quantity: number; perks?: string; soldOut?: boolean }[];
  deliveryOption?: boolean;
  pickupOption?: boolean;
  pickupAddress?: string;
  university?: string;
  // Grouping: listings that share a groupId are shown together as a collection.
  groupId?: string;
  groupTitle?: string;
}

// Deep-strip undefined so Firestore accepts the payload.
const cleanUndefined = (val: any): any => {
  if (Array.isArray(val)) return val.map(v => cleanUndefined(v));
  if (val && typeof val === 'object') {
    const out: any = {};
    Object.keys(val).forEach((k) => {
      const v = (val as any)[k];
      if (v !== undefined) out[k] = cleanUndefined(v);
    });
    return out;
  }
  return val;
};

export async function getAllListings(university?: string): Promise<Listing[]> {
  let q = collection(db, "listings");
  if (university) {
    q = query(q, where("university", "==", university));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Listing) }));
}

export async function getFeaturedListings(): Promise<Listing[]> {
  // Prefer admin-controlled featured listing IDs
  try {
    const settingsRef = doc(db, 'settings', 'featured_listings');
    const settingsSnap = await getDoc(settingsRef);
    const ids = settingsSnap.exists() ? ((settingsSnap.data() as any).listingIds as string[] ?? []) : [];
    if (ids && ids.length > 0) {
      const pulls = await Promise.all(ids.slice(0, 20).map(id => getDoc(doc(db, 'listings', id))));
      const adminSelected = pulls.filter(s => s.exists()).map(s => ({ id: s.id, ...(s.data() as Listing) }));
      // Show at most 5 on homepage
      return adminSelected.slice(0, 5);
    }
  } catch (e) {
    // ignore and fall back
  }

  // Fallback: sample 5 random verified listings client-side
  const base = query(collection(db, "listings"), where("verified", "==", true), limit(50));
  const snap = await getDocs(base);
  const all = snap.docs.map(d => ({ id: d.id, ...(d.data() as Listing) }));
  // Shuffle array (Fisher–Yates)
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  const verifiedPick = all.slice(0, 5);
  if (verifiedPick.length > 0) return verifiedPick;

  // Secondary fallback: sample 5 from any listings if none verified exist
  const anySnap = await getDocs(collection(db, "listings"));
  const anyAll = anySnap.docs.map(d => ({ id: d.id, ...(d.data() as Listing) }));
  for (let i = anyAll.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [anyAll[i], anyAll[j]] = [anyAll[j], anyAll[i]];
  }
  return anyAll.slice(0, 5);
}

export async function getListingById(id: string): Promise<Listing | null> {
  const snap = await getDoc(doc(db, "listings", id));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as Listing) }) : null;
}

export async function createListing(listing: Omit<Listing, 'sellerId' | 'seller' | 'images'>, images: File[], user: User) {
  // Enforce seller-only posting server-side
  const { role } = await getUserRole(user.uid);
  if (role !== 'seller') {
    throw new Error('Only verified sellers can post listings.');
  }

  const imageUrls = await Promise.all(images.map(image => uploadListingImage(image, user.uid)));

  const payload = {
    ...listing,
    sellerId: user.uid,
    seller: user.displayName || 'Anonymous',
    images: imageUrls,
    specifications: listing.specifications || {},
    views: 0,
    title_lower: (listing.title || '').toLowerCase(),
    category_lower: (listing.category || '').toLowerCase(),
    createdAt: serverTimestamp(),
    soldOut: false,
    ticketTiers: (listing.ticketTiers || []).map(t => ({ ...t, soldOut: t.soldOut ?? false }))
  } as any;

  const sanitized = cleanUndefined(payload);
  const ref = await addDoc(collection(db, "listings"), sanitized);

  // Notify admins of the new listing (fire-and-forget)
  notifyAdmins({
    event: 'new_listing',
    subject: `New listing posted: ${listing.title || 'Untitled'}`,
    text: `A new listing was posted on Campus Market.`,
    meta: {
      listingId: ref.id,
      sellerId: user.uid,
      sellerName: user.displayName || '',
      sellerEmail: user.email || '',
      title: listing.title || '',
      category: listing.category || '',
      price: (listing as any).price ?? '',
      imageCount: imageUrls.length,
    },
  });

  return ref.id;
}

// One draft item in a bulk upload — the image is already uploaded (URL), so publishing
// is fast. Optional shared fields (category/condition/location/group) are merged in.
export interface BulkDraft {
  imageUrl: string;
  title: string;
  price: number;
  category?: string;
  condition?: string;
  location?: string;
  description?: string;
}

export interface BulkShared {
  category?: string;
  condition?: string;
  location?: string;
  university?: string;
  groupId?: string;
  groupTitle?: string;
}

// Publishes many item listings at once from already-uploaded images. Returns the new ids.
export async function createBulkListings(
  drafts: BulkDraft[],
  user: User,
  shared: BulkShared = {}
): Promise<string[]> {
  const { role } = await getUserRole(user.uid);
  if (role !== 'seller') {
    throw new Error('Only verified sellers can post listings.');
  }

  const ids: string[] = [];
  for (const d of drafts) {
    const category = (d.category || shared.category || '').trim() || undefined;
    const payload = cleanUndefined({
      title: (d.title || '').trim(),
      price: Number(d.price) || 0,
      category,
      condition: (d.condition || shared.condition || '').trim() || undefined,
      location: (d.location || shared.location || '').trim() || undefined,
      description: (d.description || '').trim() || undefined,
      university: shared.university || undefined,
      groupId: shared.groupId || undefined,
      groupTitle: shared.groupTitle || undefined,
      type: 'item',
      sellerId: user.uid,
      seller: user.displayName || 'Anonymous',
      images: [d.imageUrl],
      specifications: {},
      views: 0,
      title_lower: (d.title || '').toLowerCase(),
      category_lower: (category || '').toLowerCase(),
      createdAt: serverTimestamp(),
      soldOut: false,
    });
    const ref = await addDoc(collection(db, 'listings'), payload);
    ids.push(ref.id);
  }

  notifyAdmins({
    event: 'new_bulk_listing',
    subject: `${ids.length} new listing(s) posted${shared.groupTitle ? ` in group "${shared.groupTitle}"` : ''}`,
    text: `A seller bulk-posted ${ids.length} item(s) on Campus Market.`,
    meta: {
      sellerId: user.uid,
      sellerName: user.displayName || '',
      sellerEmail: user.email || '',
      count: ids.length,
      groupTitle: shared.groupTitle || '',
    },
  });

  return ids;
}

export async function getSellerListings(sellerId: string): Promise<Listing[]> {
  const q = query(collection(db, "listings"), where("sellerId", "==", sellerId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Listing) }));
}

export async function getSellerEarnings(sellerId: string): Promise<number> {
  const listings = await getSellerListings(sellerId);
  return listings.reduce((total, listing) => total + (listing.price || 0), 0);
}

export async function getSellerProfileViews(sellerId: string): Promise<number> {
  // Placeholder: In a real application, this would fetch data from a database
  // For now, returning a mock value.
  console.log(`Fetching profile views for seller: ${sellerId}`);
  return Math.floor(Math.random() * 1000); // Mock value
}

export async function incrementViews(listingId: string): Promise<void> {
  const ref = doc(db, 'listings', listingId);
  await updateDoc(ref, { views: increment(1) });
}

// Generic update helper for listings. Caller can pass partial fields including ticketTiers.
export async function updateListing(listingId: string, patch: Partial<Listing>): Promise<void> {
  const cleanUndefined = (val: any): any => {
    if (Array.isArray(val)) return val.map(v => cleanUndefined(v));
    if (val && typeof val === 'object') {
      const out: any = {};
      Object.keys(val).forEach((k) => {
        const v = (val as any)[k];
        if (v !== undefined) out[k] = cleanUndefined(v);
      });
      return out;
    }
    return val;
  };
  const ref = doc(db, 'listings', listingId);
  await updateDoc(ref, cleanUndefined(patch));
}

// Convenience: mark all ticket tiers sold out or available
export async function setAllTicketTiersSoldOut(listingId: string, soldOut: boolean): Promise<void> {
  const snap = await getDoc(doc(db, 'listings', listingId));
  if (!snap.exists()) return;
  const data = snap.data() as Listing;
  const tiers = (data.ticketTiers || []).map(t => ({ ...t, soldOut }));
  await updateListing(listingId, { ticketTiers: tiers });
}

// Search listings with robust fallback: prefix on lowercased fields, then substring + synonyms
export async function searchListings(term: string, university?: string): Promise<Listing[]> {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return [];

  // Primary: prefix search on prepared lowercased fields
  try {
    let baseQuery = collection(db, 'listings');
    if (university) {
      baseQuery = query(baseQuery, where('university', '==', university));
    }

    const byTitle = query(
      baseQuery,
      orderBy('title_lower'),
      where('title_lower', '>=', normalized),
      where('title_lower', '<=', normalized + '\uf8ff'),
      limit(8)
    );
    const byCategory = query(
      baseQuery,
      orderBy('category_lower'),
      where('category_lower', '>=', normalized),
      where('category_lower', '<=', normalized + '\uf8ff'),
      limit(8)
    );

    const [titleSnap, categorySnap] = await Promise.all([getDocs(byTitle), getDocs(byCategory)]);

    const map = new Map<string, Listing>();
    titleSnap.docs.forEach(d => map.set(d.id, { id: d.id, ...(d.data() as Listing) }));
    categorySnap.docs.forEach(d => map.set(d.id, { id: d.id, ...(d.data() as Listing) }));

    const primary = Array.from(map.values());
    if (primary.length > 0) return primary;
  } catch (err) {
    // Index missing or fields absent; continue to fallback
    console.warn('Prefix search failed, using fallback', err);
  }

  // Fallback: fetch all and filter by substring across title/category/tags/description
  const all = await getAllListings(university);

  // Generate simple variations and synonyms
  const variations = new Set<string>([normalized]);
  // Power bank common variants
  if (normalized.includes('powerbank') || normalized.includes('power bank')) {
    variations.add(normalized.replace(/powerbank/g, 'power bank'));
    variations.add(normalized.replace(/power bank/g, 'powerbank'));
    variations.add('portable charger');
    variations.add('charger');
    variations.add('phone accessories');
  }
  // Generic helpers: split into tokens to match loosely
  normalized.split(/\s+/).forEach(tok => { if (tok) variations.add(tok); });

  const includesVariant = (text?: string) => {
    if (!text) return false;
    const lower = text.toLowerCase();
    for (const v of variations) {
      if (v && lower.includes(v)) return true;
    }
    return false;
  };

  const filtered = all.filter(l => {
    return (
      includesVariant(l.title) ||
      includesVariant(l.category) ||
      includesVariant(l.description) ||
      (Array.isArray(l.tags) && l.tags.some(t => includesVariant(t)))
    );
  }).slice(0, 8);

  return filtered;
}

export async function deleteListing(id: string) {
  // Prevent deletion if there are active/incomplete orders referencing this listing
  // 1) Load listing to get sellerId
  const listingSnap = await getDoc(doc(db, 'listings', id));
  if (!listingSnap.exists()) {
    // If listing no longer exists, treat as deleted
    return;
  }
  const listing = listingSnap.data() as Listing;
  const sellerId = listing.sellerId || '';

  // 2) Query active orders for this seller and check if any orderItems reference this listing
  try {
    const ordersCol = collection(db, 'orders');
    const activeStatuses = ['payment_held', 'delivery_started', 'delivered'];
    const q = query(ordersCol, where('sellerId', '==', sellerId), where('status', 'in', activeStatuses));
    const snap = await getDocs(q);
    const hasActive = snap.docs.some(d => {
      const data = d.data() as any;
      const items = Array.isArray(data.orderItems) ? data.orderItems : [];
      return items.some((it: any) => it?.listingId === id);
    });
    if (hasActive) {
      throw new Error('Cannot delete: listing has active or incomplete orders.');
    }
  } catch (err) {
    // If indexing or query fails for 'in' operator, fallback to fetching seller orders and filtering
    try {
      const qAll = query(collection(db, 'orders'), where('sellerId', '==', sellerId));
      const allSnap = await getDocs(qAll);
      const hasActive = allSnap.docs.some(d => {
        const data = d.data() as any;
        const status = String(data.status || '');
        const active = status !== 'completed' && status !== 'cancelled';
        if (!active) return false;
        const items = Array.isArray(data.orderItems) ? data.orderItems : [];
        return items.some((it: any) => it?.listingId === id);
      });
      if (hasActive) {
        throw new Error('Cannot delete: listing has active or incomplete orders.');
      }
    } catch {}
  }

  // 3) Safe to delete
  const ref = doc(db, 'listings', id);
  await deleteDoc(ref);
}
